import { useState } from 'react';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { Fingerprint, KeyRound, ShieldCheck } from 'lucide-react';
import type { LoginResponse } from '@khadi/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../components/ui';

/**
 * Anmeldung auf drei Wegen: Passwort, Passwort mit Einmalcode, oder Passkey.
 * Für die Admin-Rolle ist ein zweiter Faktor Pflicht — wer noch keinen hat,
 * richtet ihn hier direkt ein; wer einen Passkey hat, muss ihn benutzen.
 */

type Stage =
  | { kind: 'credentials' }
  | { kind: 'totp' }
  | { kind: 'passkey' }
  | { kind: 'setup'; secret: string; otpauth: string };

export function LoginPage() {
  const auth = useAuth();
  const [stage, setStage] = useState<Stage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submitCredentials(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      const result = await api<LoginResponse>('/auth/login', {
        method: 'POST',
        body: { email, password, totp: totp || undefined },
      });

      if (result.status === 'ok') {
        await auth.refresh();
        window.location.href = '/';
        return;
      }

      if (result.status === 'totp_required') {
        setStage({ kind: 'totp' });
        return;
      }

      if (result.status === 'passkey_required') {
        setStage({ kind: 'passkey' });
        return;
      }

      setStage({
        kind: 'setup',
        secret: result.setup.secret,
        otpauth: result.setup.otpauth_url,
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Anmeldung fehlgeschlagen');
      setTotp('');
    } finally {
      setBusy(false);
    }
  }

  /** Authenticator bestätigen — danach ist man angemeldet. */
  async function confirmTotpSetup(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (stage.kind !== 'setup') return;
    setBusy(true);
    setError(null);

    try {
      // Wessen Authenticator bestätigt wird, sagt die Einrichtungssitzung.
      await api('/auth/totp/confirm', {
        method: 'POST',
        body: { secret: stage.secret, code: totp },
      });
      await auth.refresh();
      window.location.href = '/';
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Der Code stimmt nicht');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Passkey während der Ersteinrichtung anlegen.
   * Er zählt als zweiter Faktor; der Authenticator wird dann nicht gebraucht.
   */
  async function registerPasskey(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const { challenge_id, options } = await api<{ challenge_id: string; options: unknown }>(
        '/auth/passkey/register/options',
        { method: 'POST' },
      );

      const response = await startRegistration({ optionsJSON: options as never });

      await api('/auth/passkey/register/verify', {
        method: 'POST',
        body: { challenge_id, response, name: deviceName() },
      });

      await auth.refresh();
      window.location.href = '/';
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Die Einrichtung des Passkeys wurde abgebrochen',
      );
    } finally {
      setBusy(false);
    }
  }

  async function loginWithPasskey(): Promise<void> {
    setBusy(true);
    setError(null);

    try {
      const { challenge_id, options } = await api<{ challenge_id: string; options: unknown }>(
        '/auth/passkey/login/options',
        { method: 'POST', body: { email: email || undefined } },
      );

      const response = await startAuthentication({ optionsJSON: options as never });

      await api('/auth/passkey/login/verify', {
        method: 'POST',
        body: { challenge_id, response },
      });

      await auth.refresh();
      window.location.href = '/';
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Die Anmeldung mit Passkey wurde abgebrochen',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-dvh place-items-center px-4 py-10">
      <div className="w-full max-w-sm">
        {/* Zeichen und Titel — dasselbe Gewebemotiv wie in der Anwendung. */}
        <div className="animate-fade-up mb-8 text-center">
          <span
            aria-hidden
            className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-[var(--radius-lg)] bg-[var(--color-khadi-forest-800)]"
          >
            <span
              className="h-7 w-7 rounded-[2px] opacity-90"
              style={{
                backgroundImage:
                  'repeating-linear-gradient(90deg, var(--color-khadi-cream-50) 0 1px, transparent 1px 4px), repeating-linear-gradient(0deg, var(--color-khadi-cream-50) 0 1px, transparent 1px 4px)',
              }}
            />
          </span>
          <h1 className="font-[family-name:var(--font-display)] text-3xl">Khadi Inventar</h1>
          <p className="mt-1 text-sm text-[var(--color-fg3)]">
            Geräte erfassen, übergeben, wiederfinden.
          </p>
        </div>

        <div className="surface animate-fade-up p-6" style={{ animationDelay: '60ms' }}>
          {stage.kind === 'passkey' ? (
            <div className="space-y-5">
              <div className="flex items-start gap-2.5 rounded-[var(--radius-md)] bg-[var(--color-khadi-saffron-100)] p-4">
                <ShieldCheck
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-khadi-saffron-600)]"
                  aria-hidden
                />
                <p className="text-[var(--color-fg1)]">
                  Für dieses Verwaltungskonto ist ein Passkey hinterlegt. Er ist der zweite
                  Faktor — das Passwort allein genügt nicht.
                </p>
              </div>

              <button
                type="button"
                onClick={() => void loginWithPasskey()}
                className="btn btn-primary btn-lg w-full"
                disabled={busy}
                autoFocus
              >
                <Fingerprint className="h-5 w-5" aria-hidden />
                Mit Passkey anmelden
              </button>
              <p className="text-center text-sm text-[var(--color-fg3)]">
                Liegt der Passkey auf dem Handy, bietet der Browser die Anmeldung per QR-Code
                an. Ist er verloren, kann ein anderer Admin ihn in der Verwaltung entfernen.
              </p>

              {error && <ErrorLine message={error} />}

              <button
                type="button"
                onClick={() => {
                  setStage({ kind: 'credentials' });
                  setError(null);
                }}
                className="btn btn-ghost btn-lg w-full"
                disabled={busy}
              >
                Zurück
              </button>
            </div>
          ) : stage.kind === 'setup' ? (
            <div className="space-y-5">
              <div className="flex items-start gap-2.5 rounded-[var(--radius-md)] bg-[var(--color-khadi-saffron-100)] p-4">
                <ShieldCheck
                  className="mt-0.5 h-5 w-5 shrink-0 text-[var(--color-khadi-saffron-600)]"
                  aria-hidden
                />
                <p className="text-[var(--color-fg1)]">
                  Für Verwaltungskonten ist ein zweiter Faktor Pflicht. Bis er steht, bleibt die
                  Anwendung gesperrt.
                </p>
              </div>

              {/* Der empfohlene Weg: ein Passkey ist schneller eingerichtet als eine
                  Authenticator-App und bei jeder Anmeldung bequemer. */}
              <div>
                <button
                  type="button"
                  onClick={() => void registerPasskey()}
                  className="btn btn-primary btn-lg w-full"
                  disabled={busy}
                >
                  <Fingerprint className="h-5 w-5" aria-hidden />
                  Passkey einrichten
                </button>
                <p className="mt-2 text-center text-sm text-[var(--color-fg3)]">
                  Fingerabdruck, Gesichtserkennung oder Geräte-PIN — empfohlen
                </p>
              </div>

              {error && <ErrorLine message={error} />}

              <details className="rounded-[var(--radius-md)] border border-[var(--color-line)] px-4 py-3">
                <summary className="cursor-pointer text-[var(--color-fg2)]">
                  Stattdessen Authenticator-App verwenden
                </summary>

                <form onSubmit={confirmTotpSetup} className="mt-4 space-y-4">
                  <div>
                    <span className="label">Schlüssel</span>
                    <code className="block rounded-[var(--radius-sm)] border border-[var(--color-line)] bg-[var(--color-bg-tinted)] px-4 py-3 font-[family-name:var(--font-mono)] text-sm break-all">
                      {stage.secret}
                    </code>
                  </div>

                  <div>
                    <label className="label" htmlFor="totp-setup">
                      Code aus der App
                    </label>
                    <input
                      id="totp-setup"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={totp}
                      onChange={(event) => setTotp(event.target.value.replace(/\D/g, ''))}
                      className="field tabular text-center text-lg tracking-[0.4em]"
                      placeholder="000000"
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-ghost btn-lg w-full"
                    disabled={busy || totp.length !== 6}
                  >
                    Authenticator bestätigen
                  </button>
                </form>
              </details>
            </div>
          ) : (
            <form onSubmit={submitCredentials} className="space-y-4">
              <div>
                <label className="label" htmlFor="email">
                  E-Mail
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="field"
                  placeholder="name@khadi.de"
                  autoFocus
                />
              </div>

              <div>
                <label className="label" htmlFor="password">
                  Passwort
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  className="field"
                />
              </div>

              {stage.kind === 'totp' && (
                <div className="animate-fade-up">
                  <label className="label" htmlFor="totp">
                    Einmalcode
                  </label>
                  <input
                    id="totp"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={totp}
                    onChange={(event) => setTotp(event.target.value.replace(/\D/g, ''))}
                    className="field tabular text-center text-lg tracking-[0.4em]"
                    placeholder="000000"
                    autoFocus
                  />
                </div>
              )}

              {error && <ErrorLine message={error} />}

              <button type="submit" className="btn btn-primary btn-lg w-full" disabled={busy}>
                <KeyRound className="h-4 w-4" aria-hidden />
                {stage.kind === 'totp' ? 'Code bestätigen' : 'Anmelden'}
              </button>

              <div className="flex items-center gap-3 py-1">
                <hr className="flex-1 border-[var(--color-line)]" />
                <span className="text-sm text-[var(--color-fg3)]">oder</span>
                <hr className="flex-1 border-[var(--color-line)]" />
              </div>

              <button
                type="button"
                onClick={() => void loginWithPasskey()}
                className="btn btn-ghost btn-lg w-full"
                disabled={busy}
              >
                <Fingerprint className="h-4 w-4" aria-hidden />
                Mit Passkey anmelden
              </button>
            </form>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-[var(--color-fg3)]">
          Zugänge vergibt Yagemi. Bei Fragen zum Konto bitte dort melden.
        </p>
      </div>
    </div>
  );
}

/** Grobe Gerätebezeichnung, damit mehrere Passkeys unterscheidbar bleiben. */
function deviceName(): string {
  const agent = navigator.userAgent;
  if (/iPhone|iPad/.test(agent)) return 'iPhone oder iPad';
  if (/Android/.test(agent)) return 'Android-Gerät';
  if (/Mac/.test(agent)) return 'Mac';
  if (/Windows/.test(agent)) return 'Windows-PC';
  return 'Dieses Gerät';
}

function ErrorLine({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className={cn(
        'animate-fade-in rounded-[var(--radius-sm)] px-5 py-4 text-sm',
        'bg-[var(--color-khadi-clay-100)] text-[var(--color-khadi-clay-700)]',
      )}
    >
      {message}
    </p>
  );
}
